/**
 * The voter search a postcard mailer is sent to.
 *
 * The Voters page saves the last search the customer ran; the Postcards page
 * reads it, so an order is placed against the customer's own list (it used to
 * be a hardcoded all-Oklahoma placeholder). localStorage so a search run in
 * another tab ("Choose voters" opens the search in a new tab) shows up here.
 */
export const MAILING_LIST_SEARCH_KEY = 'mv_mailing_list_search';

// Keys that only shape the request, never the list.
const CONTROL_KEYS = new Set(['limit', 'offset']);

export function cleanSearchFilters(filters) {
  const out = {};
  Object.keys(filters || {}).forEach((key) => {
    if (CONTROL_KEYS.has(key)) return;
    const value = filters[key];
    if (value === null || value === undefined) return;
    if (typeof value === 'string' && value.trim() === '') return;
    if (Array.isArray(value) && value.length === 0) return;
    out[key] = value;
  });
  return out;
}

export function saveMailingListSearch(filters, totalCount) {
  const clean = cleanSearchFilters(filters);
  if (!clean.State) return;
  try {
    localStorage.setItem(MAILING_LIST_SEARCH_KEY, JSON.stringify({
      filters: clean,
      totalCount: Number(totalCount) || 0,
      savedAt: Date.now(),
    }));
  } catch {
    // Storage blocked (private mode / quota): the Postcards page asks for a search.
  }
}

export function loadMailingListSearch() {
  try {
    const raw = localStorage.getItem(MAILING_LIST_SEARCH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.filters || typeof parsed.filters !== 'object' || !parsed.filters.State) return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Calls `onChange` when another tab saves a new search. Returns an unsubscribe. */
export function onMailingListSearchChange(onChange) {
  const handler = (e) => {
    if (e.key === MAILING_LIST_SEARCH_KEY) onChange(loadMailingListSearch());
  };
  window.addEventListener('storage', handler);
  return () => window.removeEventListener('storage', handler);
}

const LABELS = {
  County_Desc: 'County',
  City: 'City',
  PolitalAff: 'Party',
  CongressionalDistrict: 'Congressional district',
  CongressionalDist: 'Congressional district',
  StateSenateDistrict: 'State Senate district',
  StateSenate: 'State Senate district',
  StateHouseDistrict: 'State House district',
  StateHouse: 'State House district',
  SchoolDist: 'School district',
  MuniSub: 'Muni sub',
  Precinct: 'Precinct',
  StreetName: 'Street',
  StreetNum: 'Street number',
  FirstName: 'First name',
  LastName: 'Last name',
  VoterID: 'Voter ID',
  Status: 'Status',
};

/** One-line, human description of a saved search, e.g. "OK · County: TULSA · Age 25–65". */
export function describeMailingListSearch(filters) {
  const f = filters || {};
  const parts = [];
  if (f.State) parts.push(String(f.State).toUpperCase());
  Object.keys(LABELS).forEach((key) => {
    if (f[key] !== undefined && f[key] !== null && f[key] !== '') {
      const value = key === 'Status'
        ? ({ A: 'Active', I: 'Inactive' }[f[key]] || f[key])
        : f[key];
      parts.push(`${LABELS[key]}: ${value}`);
    }
  });
  if (f.AgeMin !== undefined || f.AgeMax !== undefined) {
    parts.push(`Age ${f.AgeMin ?? 18}–${f.AgeMax ?? 'up'}`);
  }
  if (Array.isArray(f.electionCategories) && f.electionCategories.length > 0) {
    const n = f.electionCategories.length;
    parts.push(`${n} voting-history filter${n === 1 ? '' : 's'}`);
  }
  return parts.join(' · ');
}
